import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Avatar,
  Dialog,
  Typography,
  IconButton,
  Button,
  Stack,
  Snackbar,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  apiCallGetClaimedVenueList,
  apiCallClaimedVenueUpdateStatus,
} from "../../../../Core/Apicall";
import { useLocation } from "react-router-dom";
import CommonPagination from "../../../Common/CommonPagination/CommonPagination";
import EmptyData from "../../../Common/EmptyData/EmptyData";
import CommonShimmer from "../../../Common/CommonShimmer/CommonShimmer";
import dayjs from "dayjs";

interface VenueDocument {
  documentType: string;
  documentPath: string;
}

interface Venue {
  _id: string;
  venueName: string;
  coverImage: { url: string; thumbnailUrl?: string };
  venueDocuments?: VenueDocument[];
  documentPath?: string;
  documentType?: string;
  approvalStatus: "Pending" | "Approved" | "Rejected" | "Assigned";
  createdAt?: string;
}

const VenueOwnerList: React.FC = () => {
  const [tableData, setTableData] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedMediaForPreview, setSelectedMediaForPreview] = useState<
    any | null
  >(null);

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const totalPages = Math.ceil(totalItems / rowsPerPage);

  const location = useLocation();
  const useData = location.state.user;

  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [approvalAction, setApprovalAction] = useState<
    "Approved" | "Rejected" | null
  >(null);
  const [selectedVenueId, setSelectedVenueId] = useState<string | null>(null);

  // Toast/Snackbar states
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "info" | "warning",
  });

  const handleApiCall = async (userId: any) => {
    setLoading(true);
    try {
      const data = { userId, offset: page, limit: rowsPerPage };
      const res = await apiCallGetClaimedVenueList(data);
      setTableData(res.data);
      setTotalItems(res?.totalCount || 0);
    } catch (err) {
      console.error("Failed to fetch data");
      setSnackbar({
        open: true,
        message: "Failed to fetch venue data",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleApiCall(useData.userId ? useData.userId : useData._id);
  }, [page, rowsPerPage, useData]);

  const handlePageChange = (value: number) => setPage(value);

  const confirmApproval = (
    venueId: string,
    action: "Approved" | "Rejected"
  ) => {
    setSelectedVenueId(venueId);
    setApprovalAction(action);
    setApprovalDialogOpen(true);
  };

  const executeApproval = async () => {
    if (!selectedVenueId || !approvalAction) return;
    try {
      const param = {
        venueId: selectedVenueId,
        status: approvalAction,
      };
      await apiCallClaimedVenueUpdateStatus(param);
      
      // Update local state to reflect status change immediately
      setTableData(prevData =>
        prevData.map(venue =>
          venue._id === selectedVenueId
            ? { ...venue, approvalStatus: approvalAction }
            : venue
        )
      );

      // Show success toast
      setSnackbar({
        open: true,
        message: `Venue has been ${approvalAction.toLowerCase()} successfully!`,
        severity: "success",
      });
    } catch (err) {
      console.error("Failed to update status", err);
      setSnackbar({
        open: true,
        message: "Failed to update status. Please try again.",
        severity: "error",
      });
    } finally {
      setApprovalDialogOpen(false);
      setSelectedVenueId(null);
      setApprovalAction(null);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <Box sx={{ p: 2 }}>
      <Paper sx={{ borderRadius: 3, overflow: "hidden", position: "relative" }}>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Cover Image</TableCell>
                <TableCell>Venue Name</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell>Documents</TableCell>
                <TableCell>Approval Status</TableCell>
              </TableRow>
            </TableHead>

            {!loading && (
              <TableBody>
                {tableData.map((venue) => (
                  <TableRow key={venue._id}>
                    {/* Cover Image */}
                    <TableCell>
                      <Avatar
                        variant="rounded"
                        src={venue.coverImage.url}
                        sx={{
                          width: 65,
                          height: 65,
                          cursor: "pointer",
                          borderRadius: 2,
                        }}
                        onClick={() =>
                          setSelectedMediaForPreview({
                            type: "Image",
                            url: venue.coverImage.url,
                          })
                        }
                      />
                    </TableCell>

                    <TableCell>{venue.venueName}</TableCell>

                    {/* Created At */}
                    <TableCell>
                      {venue.createdAt
                        ? dayjs(venue.createdAt).format("DD MMM YYYY, hh:mm A")
                        : "-"}
                    </TableCell>

                    {/* Documents */}
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        {venue.venueDocuments &&
                          venue.venueDocuments.length > 0 ? (
                          venue.venueDocuments.map((doc, idx) => (
                            <Avatar
                              key={idx}
                              src={doc.documentPath}
                              variant="rounded"
                              sx={{ width: 50, height: 50, cursor: "pointer" }}
                              onClick={() =>
                                setSelectedMediaForPreview({
                                  type: doc.documentType,
                                  url: doc.documentPath,
                                })
                              }
                            />
                          ))
                        ) : venue.documentPath ? (
                          <Avatar
                            src={venue.documentPath}
                            variant="rounded"
                            sx={{ width: 50, height: 50, cursor: "pointer" }}
                            onClick={() =>
                              setSelectedMediaForPreview({
                                type: venue.documentType || "Image",
                                url: venue.documentPath,
                              })
                            }
                          />
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            No Documents
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>

                    {/* Approval Status */}
                    <TableCell>
                      {venue.approvalStatus === "Pending" ? (
                        <Stack direction="row" spacing={1}>
                          <Button
                            variant="contained"
                            color="success"
                            size="small"
                            onClick={() =>
                              confirmApproval(venue._id, "Approved")
                            }
                          >
                            Approve
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={() =>
                              confirmApproval(venue._id, "Rejected")
                            }
                          >
                            Reject
                          </Button>
                        </Stack>
                      ) : (
                        <Typography
                          sx={{
                            color:
                              venue.approvalStatus === "Approved"
                                ? "green"
                                : venue.approvalStatus === "Rejected"
                                ? "red"
                                : "orange",
                            fontWeight: 600,
                          }}
                        >
                          {venue.approvalStatus}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>

          {loading && (
            <Box sx={{ p: 2 }}>
              <CommonShimmer />
            </Box>
          )}
          {!loading && <EmptyData data={tableData} />}
        </TableContainer>

        <CommonPagination
          page={page}
          rowsPerPage={rowsPerPage}
          totalItems={totalItems}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          onRowsPerPageChange={(value) => {
            setRowsPerPage(value);
            setPage(1);
          }}
        />
      </Paper>

      {/* Media Preview Dialog */}
      <Dialog
        open={!!selectedMediaForPreview}
        onClose={() => setSelectedMediaForPreview(null)}
        maxWidth="md"
      >
        <IconButton
          onClick={() => setSelectedMediaForPreview(null)}
          sx={{ position: "absolute", top: 10, right: 10, zIndex: 10 }}
        >
          <CloseIcon />
        </IconButton>

        {selectedMediaForPreview?.type === "Image" && (
          <img
            src={selectedMediaForPreview.url}
            alt="Preview"
            style={{ width: "100%", maxHeight: "50vh", objectFit: "contain" }}
          />
        )}
      </Dialog>

      {/* Approval Confirmation Dialog */}
      <Dialog
        open={approvalDialogOpen}
        onClose={() => setApprovalDialogOpen(false)}
      >
        <Box sx={{ p: 3, textAlign: "center", minWidth: "50vh", objectFit: "contain" }}>
          <Typography variant="h6" gutterBottom>
            {approvalAction === "Approved" ? "Approve Venue" : "Reject Venue"}
          </Typography>
          <Typography variant="body1">
            Are you sure you want to{" "}
            <strong>{approvalAction?.toLowerCase()}</strong> the venue{" "}
            <strong>
              {tableData.find((v) => v._id === selectedVenueId)?.venueName}
            </strong>
            ?
          </Typography>
          <Stack
            direction="row"
            spacing={2}
            justifyContent="center"
            sx={{ mt: 3 }}
          >
            <Button
              variant="contained"
              color={approvalAction === "Approved" ? "success" : "error"}
              onClick={executeApproval}
            >
              Yes, {approvalAction?.toLowerCase()}
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => setApprovalDialogOpen(false)}
            >
              Cancel
            </Button>
          </Stack>
        </Box>
      </Dialog>

      {/* Snackbar/Toast for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VenueOwnerList;